import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest, withApi } from "../utils/api";

// Sube el soporte a la carpeta privada `incapacities` y devuelve la URL a guardar en imageLink.
const uploadIncapacityImage = async (file, token) => {
  const formData = new FormData();
  formData.append("image", file);
  formData.append("folder", "incapacities");
  const data = await apiRequest("/media/images", {
    method: "POST",
    body: formData,
    token,
  });
  return data?.url;
};

// Incapacidades propias (GET /incapacities/me). imageLink llega como enlace firmado temporal.
export const fetchIncapacities = createAsyncThunk(
  "incapacity/fetchIncapacities",
  withApi(async (_, { token }) => {
    const data = await apiRequest("/incapacities/me", { token });
    return Array.isArray(data) ? data : [];
  })
);

export const createIncapacity = createAsyncThunk(
  "incapacity/createIncapacity",
  withApi(async ({ startDate, endDate, reason, imageFile }, { token }) => {
    const body = {
      dateRange: { startDate, endDate },
      reason,
    };
    if (imageFile) {
      body.imageLink = await uploadIncapacityImage(imageFile, token);
    }

    const { data, message } = await apiRequest("/incapacities", {
      method: "POST",
      body,
      token,
      withMessage: true,
    });
    return {
      message: message || "Incapacidad registrada",
      data: data || null,
    };
  })
);

export const updateIncapacity = createAsyncThunk(
  "incapacity/updateIncapacity",
  withApi(async ({ id, startDate, endDate, reason, imageFile }, { token }) => {
    const body = {
      dateRange: { startDate, endDate },
      reason,
    };
    if (imageFile) {
      body.imageLink = await uploadIncapacityImage(imageFile, token);
    }

    const { data, message } = await apiRequest(`/incapacities/${id}`, {
      method: "PATCH",
      body,
      token,
      withMessage: true,
    });
    return {
      message: message || "Incapacidad actualizada",
      data: data || null,
    };
  })
);

const incapacitySlice = createSlice({
  name: "incapacity",
  initialState: {
    incapacities: [],
    statusFetchIncapacities: "idle",
    errorFetchIncapacities: null,
    statusCreateIncapacity: "idle",
    errorCreateIncapacity: null,
    messageCreateIncapacity: null,
    statusUpdateIncapacity: "idle",
    errorUpdateIncapacity: null,
    messageUpdateIncapacity: null,
  },
  reducers: {
    setInitialIncapacities: (state, action) => {
      state.incapacities = Array.isArray(action.payload) ? action.payload : [];
    },
    clearIncapacityFeedback: (state) => {
      state.errorCreateIncapacity = null;
      state.messageCreateIncapacity = null;
      state.statusCreateIncapacity = "idle";
      state.errorUpdateIncapacity = null;
      state.messageUpdateIncapacity = null;
      state.statusUpdateIncapacity = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIncapacities.pending, (state) => {
        state.statusFetchIncapacities = "loading";
        state.errorFetchIncapacities = null;
      })
      .addCase(fetchIncapacities.fulfilled, (state, action) => {
        state.statusFetchIncapacities = "succeeded";
        state.incapacities = action.payload;
      })
      .addCase(fetchIncapacities.rejected, (state, action) => {
        state.statusFetchIncapacities = "failed";
        state.errorFetchIncapacities =
          action.payload || "No se pudieron obtener las incapacidades";
      })
      .addCase(createIncapacity.pending, (state) => {
        state.statusCreateIncapacity = "loading";
        state.errorCreateIncapacity = null;
        state.messageCreateIncapacity = null;
      })
      .addCase(createIncapacity.fulfilled, (state, action) => {
        state.statusCreateIncapacity = "succeeded";
        state.errorCreateIncapacity = null;
        state.messageCreateIncapacity = action.payload?.message || null;

        if (action.payload?.data) {
          state.incapacities.unshift(action.payload.data);
        }
      })
      .addCase(createIncapacity.rejected, (state, action) => {
        state.statusCreateIncapacity = "failed";
        state.messageCreateIncapacity = null;
        state.errorCreateIncapacity =
          action.payload || action.error?.message || "No se pudo crear la incapacidad";
      })
      .addCase(updateIncapacity.pending, (state) => {
        state.statusUpdateIncapacity = "loading";
        state.errorUpdateIncapacity = null;
        state.messageUpdateIncapacity = null;
      })
      .addCase(updateIncapacity.fulfilled, (state, action) => {
        state.statusUpdateIncapacity = "succeeded";
        state.errorUpdateIncapacity = null;
        state.messageUpdateIncapacity = action.payload?.message || null;

        if (action.payload?.data) {
          const updated = action.payload.data;
          const index = state.incapacities.findIndex((i) => i._id === updated._id);
          if (index !== -1) {
            state.incapacities[index] = updated;
          }
        }
      })
      .addCase(updateIncapacity.rejected, (state, action) => {
        state.statusUpdateIncapacity = "failed";
        state.messageUpdateIncapacity = null;
        state.errorUpdateIncapacity =
          action.payload || action.error?.message || "No se pudo actualizar la incapacidad";
      });
  },
});

export const { setInitialIncapacities, clearIncapacityFeedback } = incapacitySlice.actions;
export default incapacitySlice.reducer;
